import React, { useEffect, useState } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import { Label, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import { Button, Col, RSelect, Row } from "../../components/Component";
import { GET, POST } from "../../helper/api";
import { INVENTORY_TYPE, renderLabelIngredientPriceHistoryOption, USER_ROLES } from "../../utils/Utils";
import { useUser } from "../../context/user";
import { WAREHOUSE_OPTIONS } from "./utils";
const SHIFT_OPTIONS = [
  { label: "Cả ngày", value: "CẢ NGÀY" },
  { label: "Buổi sáng", value: "SÁNG" },
  { label: "Buổi trưa", value: "TRƯA" },
  { label: "Buổi chiều/tối", value: "CHIỀU" },
];

const SOURCE_TABS = [
  { label: "Đợt hàng (IPH)", value: "iph" },
  { label: "Hợp đồng ngoài", value: "bec" },
  { label: "Chuyển kho nội bộ", value: "internal" },
];

const TYPE_OPTIONS = Object.keys(INVENTORY_TYPE).map((c) => ({
  label: INVENTORY_TYPE[c],
  value: c,
}));


const UpsertIngredientSchedule = ({ isOpen, onClose, onSuccess }) => {
  const {
    state: { user: currentUser },
  } = useUser();
  const isManager = [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.ENTRY_STAFF].includes(
    currentUser?.role,
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: { startShift: "CẢ NGÀY", isNeedPickup: false, type: "IN" } });

  const [loading, setLoading] = useState(false);
  const [iphOptions, setIphOptions] = useState([]);
  const [becOptions, setBecOptions] = useState([]);
  const [ingredientOptions, setIngredientOptions] = useState([]);

  const [deliveryProviderOptions, setDeliveryProviderOptions] = useState([]);
  const [sourceTab, setSourceTab] = useState("iph");

  const isNeedPickup = watch("isNeedPickup");
  const type = watch("type");

  useEffect(() => {
    if (isOpen) {
      reset({ startShift: "CẢ NGÀY", isNeedPickup: false, type: "IN" });
      setSourceTab("iph");
      loadIphOptions();
      loadBecOptions();
      loadIngredientOptions();
      loadDeliveryProviderOptions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (sourceTab === "bec") {
      setValue("type", "OUT");
    }
    if (sourceTab === "internal") {
      setValue("ingredientPriceHistoryId", null);
      setValue("billExternalContractId", null);
    } else {
      setValue("internalTransferIngredientId", null);
      setValue("internalTransferFromWarehouseId", null);
      setValue("internalTransferToWarehouseId", null);
    }
  }, [sourceTab, setValue]);

  const iphOptionsByType = iphOptions.filter((o) => o.data.type === type);

  const filterIphOption = (option, inputValue) => {
    if (!inputValue) return true;
    const q = inputValue.toLowerCase();
    const d = option.data?.data;
    return (
      String(d?.customerId || "").toLowerCase().includes(q) ||
      (d?.customer?.name || "").toLowerCase().includes(q) ||
      String(d?.ingredientId || "").toLowerCase().includes(q) ||
      (d?.ingredient?.name || "").toLowerCase().includes(q)
    );
  };

  const loadIphOptions = () => {
    GET("/ingredient-price-history", { status: "APPROVED" }, true)
      .then(({ data }) => {
        const rows = data?.rows || data || [];
        setIphOptions(
          rows.map((row) => ({
            label: (
              <>
                {row.ingredient?.name || row.ingredientId} — {row.customer?.name || row.customerId} —{" "}
                {renderLabelIngredientPriceHistoryOption(row)}
              </>
            ),
            value: row.id,
            data: row,
          })),
        );
      })
      .catch(() => { });
  };


  const loadBecOptions = () => {
    GET("/bill-external-contract", { notCompleted: true }, true)
      .then(({ data }) => {
        const rows = data?.rows || data || [];
        setBecOptions(
          rows.map((row) => ({
            label: `${row.name} — ${row.customer?.name || row.customerId} — ${row.ingredient?.name || row.ingredientId}`,
            value: row.id,
          })),
        );
      })
      .catch(() => { });
  };

  const loadIngredientOptions = () => {
    GET("ingredient", {}, true)
      .then(({ data }) => {
        const rows = data?.rows || data || [];
        setIngredientOptions(rows.map((c) => ({ label: c.name, value: c.id })));
      })
      .catch(() => { });
  };

  const loadDeliveryProviderOptions = () => {
    GET("/customer", { isDeliveryProvider: true }, true)
      .then(({ data }) => {
        const rows = data?.rows || data || [];
        setDeliveryProviderOptions(rows.map((c) => ({ label: c.name, value: c.id })));
      })
      .catch(() => { });
  };

  const onSubmit = async (formData) => {
    const payload = {
      quantity: formData.quantity,
      startDate: formData.startDate,
      startShift: formData.startShift,
      isNeedPickup: formData.isNeedPickup || false,
      note: formData.note || undefined,
    };

    if (sourceTab === "iph") {
      payload.ingredientPriceHistoryId = formData.ingredientPriceHistoryId;
    } else if (sourceTab === "bec") {
      payload.billExternalContractId = formData.billExternalContractId;
    } else if (sourceTab === "internal") {
      payload.internalData = {
        internalTransferFromWarehouseId: formData.internalTransferFromWarehouseId,
        internalTransferToWarehouseId: formData.internalTransferToWarehouseId,
        internalTransferIngredientId: formData.internalTransferIngredientId,
      };
    }

    if (sourceTab !== "internal" && formData.isNeedPickup) {
      payload.deliveryProviderId = formData.deliveryProviderId;
      payload.warehouseId = formData.warehouseId;
    }

    setLoading(true);
    try {
      await POST("/ingredient-schedule", payload, {}, true);
      toast.success("Tạo lịch thành công");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.error || "Có lỗi xảy ra");
    } finally {
      setLoading(false);
    }
  };

  if (!isManager) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader>Tạo lịch hàng mới</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-3">
            {/* Source tab toggle */}
            <Col md="12">
              <div className="form-group">
                <Label className="form-label">Nguồn hàng</Label>
                <div className="d-flex gap-2">
                  {SOURCE_TABS.map((tab) => (
                    <Button
                      key={tab.value}
                      className={`${sourceTab === tab.value ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSourceTab(tab.value)}
                    >
                      {tab.label}
                    </Button>
                  ))}
                </div>
              </div>
            </Col>

            {/* Type (IN/OUT) — ẩn khi chuyển kho nội bộ */}
            {sourceTab !== "internal" && (
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">
                    Loại <span className="text-danger">*</span>
                  </label>
                  <div className="form-control-wrap">
                    <Controller
                      control={control}
                      name="type"
                      rules={{ required: sourceTab !== "internal" ? "Bắt buộc" : false }}
                      render={({ onChange, value }) => (
                        <RSelect
                          isDisabled={sourceTab === "bec"}
                          options={TYPE_OPTIONS}
                          value={TYPE_OPTIONS.find((o) => o.value === value) || null}
                          onChange={(selected) => {
                            onChange(selected?.value);
                            setValue("ingredientPriceHistoryId", null);
                          }}
                          placeholder="-- Chọn loại --"
                        />
                      )}
                    />
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="type"
                    render={({ message }) => <span className="invalid">{message}</span>}
                  />
                </div>
              </Col>
            )}

            {/* IPH select */}
            {sourceTab === "iph" && (
              <Col md="12">
                <div className="form-group">
                  <label className="form-label">
                    Đợt hàng <span className="text-danger">*</span>
                  </label>
                  <div className="form-control-wrap">
                    <Controller
                      control={control}
                      name="ingredientPriceHistoryId"
                      rules={{ required: sourceTab === "iph" ? "Bắt buộc" : false }}
                      render={({ onChange, value }) => (
                        <RSelect
                          options={iphOptionsByType}
                          value={iphOptionsByType.find((o) => o.value === value) || null}
                          onChange={(selected) => onChange(selected?.value)}
                          placeholder="-- Tìm theo tên/ID khách hàng, nguyên liệu --"
                          filterOption={filterIphOption}
                        />
                      )}
                    />
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="ingredientPriceHistoryId"
                    render={({ message }) => <span className="invalid">{message}</span>}
                  />
                </div>
              </Col>
            )}

            {/* Chuyển kho nội bộ */}
            {sourceTab === "internal" && (
              <>
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">
                      Nguyên liệu <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="internalTransferIngredientId"
                        rules={{ required: sourceTab === "internal" ? "Bắt buộc" : false }}
                        render={({ onChange, value }) => (
                          <RSelect
                            options={ingredientOptions}
                            value={ingredientOptions.find((o) => o.value === value) || null}
                            onChange={(selected) => onChange(selected?.value)}
                            placeholder="-- Chọn nguyên liệu --"
                          />
                        )}
                      />
                    </div>
                    <ErrorMessage
                      errors={errors}
                      name="internalTransferIngredientId"
                      render={({ message }) => <span className="invalid">{message}</span>}
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">
                      Kho nguồn <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="internalTransferFromWarehouseId"
                        rules={{ required: sourceTab === "internal" ? "Bắt buộc" : false }}
                        render={({ onChange, value }) => (
                          <RSelect
                            options={WAREHOUSE_OPTIONS}
                            value={WAREHOUSE_OPTIONS.find((o) => o.value === value) || null}
                            onChange={(selected) => onChange(selected?.value)}
                            placeholder="-- Chọn kho nguồn --"
                          />
                        )}
                      />
                    </div>
                    <ErrorMessage
                      errors={errors}
                      name="internalTransferFromWarehouseId"
                      render={({ message }) => <span className="invalid">{message}</span>}
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">
                      Kho đích <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="internalTransferToWarehouseId"
                        rules={{ required: sourceTab === "internal" ? "Bắt buộc" : false }}
                        render={({ onChange, value }) => (
                          <RSelect
                            options={WAREHOUSE_OPTIONS}
                            value={WAREHOUSE_OPTIONS.find((o) => o.value === value) || null}
                            onChange={(selected) => onChange(selected?.value)}
                            placeholder="-- Chọn kho đích --"
                          />
                        )}
                      />
                    </div>
                    <ErrorMessage
                      errors={errors}
                      name="internalTransferToWarehouseId"
                      render={({ message }) => <span className="invalid">{message}</span>}
                    />
                  </div>
                </Col>
              </>
            )}

            {/* Bill External Contract select */}
            {sourceTab === "bec" && (
              <Col md="12">
                <div className="form-group">
                  <label className="form-label">
                    Hợp đồng ngoài <span className="text-danger">*</span>
                  </label>
                  <div className="form-control-wrap">
                    <Controller
                      control={control}
                      name="billExternalContractId"
                      rules={{ required: sourceTab === "bec" ? "Bắt buộc" : false }}
                      render={({ onChange, value }) => (
                        <RSelect
                          options={becOptions}
                          value={becOptions.find((o) => o.value === value) || null}
                          onChange={(selected) => onChange(selected?.value)}
                          placeholder="-- Chọn hợp đồng ngoài --"
                        />
                      )}
                    />
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="billExternalContractId"
                    render={({ message }) => <span className="invalid">{message}</span>}
                  />
                </div>
              </Col>
            )}

            {/* Quantity */}
            <Col md="6">
              <div className="form-group">
                <label className="form-label">
                  Số lượng (kg) <span className="text-danger">*</span>
                </label>
                <div className="form-control-wrap">
                  <Controller
                    control={control}
                    name="quantity"
                    rules={{ required: "Bắt buộc", min: { value: 1, message: "Phải > 0" } }}
                    render={({ onChange, value, ref }) => (
                      <NumericFormat
                        className="form-control"
                        inputRef={ref}
                        type="text"
                        value={value}
                        thousandsGroupStyle="thousand"
                        thousandSeparator=","
                        decimalScale={2}
                        onValueChange={(val) => onChange(val.floatValue)}
                        allowNegative={false}
                        placeholder="Nhập số lượng"
                      />
                    )}
                  />
                </div>
                <ErrorMessage
                  errors={errors}
                  name="quantity"
                  render={({ message }) => <span className="invalid">{message}</span>}
                />
              </div>
            </Col>

            {/* Date */}
            <Col md="6">
              <div className="form-group">
                <label className="form-label">
                  Ngày <span className="text-danger">*</span>
                </label>
                <div className="form-control-wrap">
                  <input
                    type="date"
                    className="form-control"
                    name="startDate"
                    ref={register({ required: "Bắt buộc" })}
                  />
                </div>
                <ErrorMessage
                  errors={errors}
                  name="startDate"
                  render={({ message }) => <span className="invalid">{message}</span>}
                />
              </div>
            </Col>

            {/* Shift */}
            <Col md="6">
              <div className="form-group">
                <label className="form-label">
                  Buổi <span className="text-danger">*</span>
                </label>
                <div className="form-control-wrap">
                  <Controller
                    control={control}
                    name="startShift"
                    rules={{ required: "Bắt buộc" }}
                    render={({ onChange, value }) => (
                      <RSelect
                        options={SHIFT_OPTIONS}
                        value={SHIFT_OPTIONS.find((o) => o.value === value) || null}
                        onChange={(selected) => onChange(selected?.value)}
                        placeholder="-- Chọn buổi --"
                      />
                    )}
                  />
                </div>
                <ErrorMessage
                  errors={errors}
                  name="startShift"
                  render={({ message }) => <span className="invalid">{message}</span>}
                />
              </div>
            </Col>

            {/* isNeedPickup checkbox — ẩn khi chuyển kho nội bộ */}
            {sourceTab !== "internal" && (
              <Col md="6">
                <div className="form-group d-flex align-items-center" style={{ marginTop: 28 }}>
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="isNeedPickup"
                      name="isNeedPickup"
                      ref={register()}
                    />
                    <label className="custom-control-label" htmlFor="isNeedPickup">
                      Cần điều xe / vận chuyển
                    </label>
                  </div>
                </div>
              </Col>
            )}

            {/* Delivery provider — only shown when isNeedPickup */}
            {sourceTab !== "internal" && isNeedPickup && (
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">
                    Đơn vị vận chuyển <span className="text-danger">*</span>
                  </label>
                  <div className="form-control-wrap">
                    <Controller
                      control={control}
                      name="deliveryProviderId"
                      rules={{ required: isNeedPickup ? "Bắt buộc khi cần điều xe" : false }}
                      render={({ onChange, value }) => (
                        <RSelect
                          options={deliveryProviderOptions}
                          value={deliveryProviderOptions.find((o) => o.value === value) || null}
                          onChange={(selected) => onChange(selected?.value)}
                          placeholder="-- Chọn đơn vị vận chuyển --"
                        />
                      )}
                    />
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="deliveryProviderId"
                    render={({ message }) => <span className="invalid">{message}</span>}
                  />
                </div>
              </Col>
            )}

            {/* Warehouse — only shown when isNeedPickup */}
            {sourceTab !== "internal" && isNeedPickup && (
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">
                    Kho <span className="text-danger">*</span>
                  </label>
                  <div className="form-control-wrap">
                    <Controller
                      control={control}
                      name="warehouseId"
                      rules={{ required: isNeedPickup ? "Bắt buộc khi cần điều xe" : false }}
                      render={({ onChange, value }) => (
                        <RSelect
                          options={WAREHOUSE_OPTIONS}
                          value={WAREHOUSE_OPTIONS.find((o) => o.value === value) || null}
                          onChange={(selected) => onChange(selected?.value)}
                          placeholder="-- Chọn kho --"
                        />
                      )}
                    />
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="warehouseId"
                    render={({ message }) => <span className="invalid">{message}</span>}
                  />
                </div>
              </Col>
            )}

            {/* Note */}
            <Col md="12">
              <div className="form-group">
                <label className="form-label">Ghi chú</label>
                <div className="form-control-wrap">
                  <textarea
                    className="form-control"
                    name="note"
                    rows={2}
                    placeholder="Ghi chú thêm (không bắt buộc)"
                    ref={register()}
                  />
                </div>
              </div>
            </Col>
          </Row>

          <div className="mt-3 d-flex justify-content-end gap-2">
            <Button color="light" type="button" size="lg" onClick={onClose}>
              Huỷ
            </Button>
            <Button color="primary" type="submit" size="lg" disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Tạo mới"}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default UpsertIngredientSchedule;
